import apiClient from "./client";
import { hasMinRole } from "@/lib/roles";

/**
 * Code Companion (agente 3) — board do owner. Tipos escritos à mão (o openapi.json
 * versionado é regenerado à parte); todas as chamadas são cookie-auth via apiClient.
 */

export type CcPackageStatus =
  | "backlog"
  | "approved"
  | "in_progress"
  | "ready_for_deploy"
  | "done"
  | "blocked";

export interface CcPackageSummary {
  id: number;
  devclass: string;
  title: string | null;
  status: CcPackageStatus;
  assigned_dev_id: string | null;
  assigned_dev_name: string | null;
  object_count: number;
  in_scope_count: number;
  progress_pct: number;
  scope_approved: boolean;
}

export interface CcPackageObject {
  obj_name: string;
  in_scope: boolean;
  status: "pending" | "in_progress" | "done";
  progress_pct: number;
  wricef: string | null;
  criticality_score: number | null;
}

export interface CcPackageDetail {
  id: number;
  devclass: string;
  title: string | null;
  status: CcPackageStatus;
  assigned_dev_id: string | null;
  progress_pct: number;
  scope_approved: boolean;
  objects: CcPackageObject[];
}

export interface CcActivityItem {
  id: number;
  type: string;
  package_id: number | null;
  object_id: number | null;
  actor_id: string | null;
  payload: Record<string, unknown> | null;
  created_at: string;
}

export type CcFlagKind = "context_incorrect" | "to_verify" | "blocker";

export interface CcFlag {
  id: number;
  package_id: number;
  object_id: number | null;
  kind: CcFlagKind;
  note: string | null;
  status: "open" | "resolved";
  raised_by: string | null;
  created_at: string;
  resolved_at: string | null;
}

export interface CcArtifact {
  id: number;
  name: string;
  artifact_type: string | null;
  storage_path: string | null;
  object_ids: number[];
}

export interface CcContext {
  package_id: number;
  version: number;
  markdown: string;
  claude_md: string;
  bundle_path: string | null;
  built_at: string;
  change_summary: string | null;
}

export interface CcSupplement {
  id: number;
  package_id: number;
  title: string;
  body: string;
  flag_id: number | null;
  created_at: string;
}

export interface CcDeveloper {
  user_id: string;
  name: string | null;
  email: string | null;
  linked_via: "direct" | "team";
  has_active_grant: boolean;
}

const base = (projectId: string) => `/api/projects/${projectId}/code-companion`;

// ── Board ──────────────────────────────────────────────────────────────────
export async function seedBoard(
  projectId: string,
): Promise<{ packages: number; objects: number }> {
  const res = await apiClient.post(`${base(projectId)}/seed`);
  return res.data;
}

export async function listPackages(
  projectId: string,
): Promise<{ total: number; items: CcPackageSummary[] }> {
  const res = await apiClient.get(`${base(projectId)}/packages`);
  return res.data;
}

export async function getPackage(
  projectId: string,
  packageId: number,
): Promise<CcPackageDetail> {
  const res = await apiClient.get(`${base(projectId)}/packages/${packageId}`);
  return res.data;
}

export async function setAssignee(
  projectId: string,
  packageId: number,
  userId: string | null,
): Promise<CcPackageSummary> {
  const res = await apiClient.patch(
    `${base(projectId)}/packages/${packageId}/assignee`,
    { user_id: userId },
  );
  return res.data;
}

export async function setStatus(
  projectId: string,
  packageId: number,
  status: CcPackageStatus,
): Promise<{ id: number; status: CcPackageStatus }> {
  const res = await apiClient.patch(
    `${base(projectId)}/packages/${packageId}/status`,
    { status },
  );
  return res.data;
}

export interface CcTechnicalScope {
  approved: boolean;
  approved_at: string | null;
  approved_by_name: string | null;
  in_scope_count: number;
  total_effort_h: number;
  /** Pendente de validação com o cliente: fora do total, nunca somado (#1503). */
  pending_count: number;
  pending_effort_h: number;
  /** Objeto com letra WRICEF sem nenhuma decisão do arquiteto ainda. */
  undecided_count: number;
  /** `"decision"` com decisão gravada no projeto; `"legacy_disposition"` no
   *  fallback de hoje (corrigido) para quem ainda não decidiu nada. */
  scope_source: "decision" | "legacy_disposition";
}

/** Gate A (Architect): status da aprovação técnica + tamanho do escopo. */
export async function getTechnicalScope(projectId: string): Promise<CcTechnicalScope> {
  const res = await apiClient.get(`${base(projectId)}/technical-scope`);
  return res.data;
}

/** Gate A: aprova o escopo técnico e auto-semeia o board do CC. */
export async function approveTechnicalScope(projectId: string): Promise<CcTechnicalScope> {
  const res = await apiClient.post(`${base(projectId)}/approve-technical-scope`);
  return res.data;
}

export async function approveContext(
  projectId: string,
  packageId: number,
): Promise<{ id: number; status: CcPackageStatus; context_version: number }> {
  const res = await apiClient.post(
    `${base(projectId)}/packages/${packageId}/approve-context`,
  );
  return res.data;
}

export async function markReady(
  projectId: string,
  packageId: number,
  deployLocation: string,
): Promise<{ id: number; status: CcPackageStatus; deploy_location: string }> {
  const res = await apiClient.post(
    `${base(projectId)}/packages/${packageId}/ready`,
    { deploy_location: deployLocation },
  );
  return res.data;
}

/** Uma linha do gate: um objeto in-scope e o veredito dele. */
export interface CcReadyGateObject {
  object_name: string;
  /** `abap` cobra change set + deploy; outro canal cobra pacote de handoff (CC.30). */
  channel: string;
  ok: boolean;
  reason: string | null;
}

/** Um caso de paridade na leitura do gate. */
export interface CcReadyGateRegression {
  case_id: string;
  verdict: string;
  /** Provou o comportamento. `dropped` é `false`: retirado do escopo não é verificado. */
  ok: boolean;
  /** Barra a promoção. Não é o inverso de `ok` — retirado do escopo não prova e não barra. */
  blocking: boolean;
  object_name: string | null;
  /** Por que barra, em linguagem de gente. Antes a tela mostrava o valor da coluna. */
  reason: string | null;
  /** `run_gates`, `fix_object`, ou `null` quando não há conserto conhecido. */
  remedy: string | null;
  /** `null` = registrado fora do fluxo; nenhuma execução pode quitá-lo. */
  test_method: string | null;
  /** De onde veio o esperado. Hoje sempre `llm`: o ECC não é executado. */
  expected_source: string | null;
  /** Como se soube o veredito: `abap_unit`, `mcp` ou `human`. */
  evidence_source: string | null;
}

export interface CcReadyGate {
  ok: boolean;
  package_id: number;
  /** Frases prontas, o mesmo texto que a recusa do POST devolve. */
  missing: string[];
  /**
   * Nunca bloqueiam, e por isso mesmo precisam aparecer inclusive com o gate verde.
   * Promover sem prova de comportamento é decisão legítima; esconder que ela foi tomada
   * faz o dossiê parecer mais forte do que é.
   */
  warnings: string[];
  objects: CcReadyGateObject[];
  regressions: CcReadyGateRegression[];
}

/**
 * O que falta para o pacote poder ser promovido, sem promover (CC.34 F5).
 *
 * Mesmo cálculo que o `markReady` aplica no servidor. A tela não reimplementa a
 * regra de propósito: reimplementar faria o painel mentir no dia em que o gate
 * mudasse.
 */
export async function getReadyGate(
  projectId: string,
  packageId: number,
): Promise<CcReadyGate> {
  const res = await apiClient.get(
    `${base(projectId)}/packages/${packageId}/ready-gate`,
  );
  return res.data;
}

/** Um evento do ledger append-only do pacote. */
export interface CcEvidenceEvent {
  id: number;
  event_type: string;
  object_name: string | null;
  payload: Record<string, unknown> | null;
  /** ⚠️ Só vem na leitura de evento único (#513). Na lista use `has_sap_raw`. */
  sap_raw?: Record<string, unknown> | null;
  /** Se este evento tem resposta crua do SAP, para a tela oferecer sem pedir. */
  has_sap_raw?: boolean;
  /**
   * sha256 do conteúdo DESTE evento. Não é cadeia encadeada: confere que o evento
   * não mudou, não prova a integridade da sequência. O rótulo na tela precisa dizer
   * "hash do conteúdo" e não "cadeia".
   */
  hash: string;
  created_at: string | null;
  /**
   * Quem escreveu a linha: `autonomous` (o run), `mcp` (o terminal do dev) ou `human`.
   *
   * A coluna entrou na S-064 onda 2 e a leitura do dossiê só passou a selecioná-la na
   * onda 7 — dado gravado e não lido, o mesmo padrão que esta spec persegue. `null` nas
   * linhas anteriores à onda 2.
   */
  actor_type?: CcActorType | null;
}

export interface CcEvidenceDossier {
  package_id: number;
  /** Quantos eventos o filtro alcança, não quantos vieram nesta página. `truncated` diz se cortou. */
  total: number;
  /** Se há próxima página. Sai do cursor, e não da contagem: com o total filtrado,
   *  `total > len(events)` fazia toda página depois da primeira se dizer cortada. */
  truncated: boolean;
  /** Id da última linha desta página, ou `null` quando acabou. */
  next_cursor?: number | null;
  events: CcEvidenceEvent[];
}

/** O dossiê do pacote: o que aconteceu, do mais novo pro mais velho (CC.34 F8). */
/** Um evento do dossiê, com a resposta crua do SAP (#513).
 *
 * O cru sai da lista de propósito: o ledger grava uma linha por fase por objeto, e a
 * resposta inteira do ADT em toda linha faz o dossiê pesar megabytes para mostrar
 * títulos. Quem abre um evento quer conferir aquele.
 */
export async function getEvidenceEvent(
  projectId: string,
  packageId: number,
  eventId: number,
): Promise<CcEvidenceEvent> {
  const res = await apiClient.get(
    `${base(projectId)}/packages/${packageId}/evidence/${eventId}`,
  );
  return res.data;
}

export async function getEvidence(
  projectId: string,
  packageId: number,
  filtros?: { objectName?: string; eventType?: string; cursor?: number },
): Promise<CcEvidenceDossier> {
  const res = await apiClient.get(`${base(projectId)}/packages/${packageId}/evidence`, {
    params: {
      object_name: filtros?.objectName,
      event_type: filtros?.eventType,
      cursor: filtros?.cursor,
    },
  });
  return res.data;
}

export async function listArtifacts(
  projectId: string,
  packageId: number,
): Promise<{ total: number; items: CcArtifact[] }> {
  const res = await apiClient.get(
    `${base(projectId)}/packages/${packageId}/artifacts`,
  );
  return res.data;
}

export async function getContext(
  projectId: string,
  packageId: number,
): Promise<CcContext> {
  const res = await apiClient.get(
    `${base(projectId)}/packages/${packageId}/context`,
  );
  return res.data;
}

// ── Curadoria de contexto (CC.6) ─────────────────────────────────────────────
export async function rebuildContext(
  projectId: string,
  packageId: number,
): Promise<CcContext> {
  const res = await apiClient.post(
    `${base(projectId)}/packages/${packageId}/context/build`,
  );
  return res.data;
}

export async function listSupplements(
  projectId: string,
  packageId: number,
): Promise<{ total: number; items: CcSupplement[] }> {
  const res = await apiClient.get(
    `${base(projectId)}/packages/${packageId}/context/supplements`,
  );
  return res.data;
}

export async function addSupplement(
  projectId: string,
  packageId: number,
  body: { title: string; body: string; flag_id?: number },
): Promise<CcSupplement> {
  const res = await apiClient.post(
    `${base(projectId)}/packages/${packageId}/context/supplements`,
    body,
  );
  return res.data;
}

// ── Tracking ─────────────────────────────────────────────────────────────────
export async function listActivity(
  projectId: string,
  opts?: { packageId?: number; limit?: number },
): Promise<{ total: number; items: CcActivityItem[] }> {
  const res = await apiClient.get(`${base(projectId)}/activity`, {
    params: { package_id: opts?.packageId, limit: opts?.limit },
  });
  return res.data;
}

export async function listFlags(
  projectId: string,
  status: "open" | "resolved" | "all" = "open",
): Promise<{ total: number; items: CcFlag[] }> {
  const res = await apiClient.get(`${base(projectId)}/flags`, {
    params: { status },
  });
  return res.data;
}

export async function resolveFlag(
  projectId: string,
  flagId: number,
): Promise<{ id: number; status: string }> {
  const res = await apiClient.post(
    `${base(projectId)}/flags/${flagId}/resolve`,
  );
  return res.data;
}

// ── Access (para o dropdown de reatribuição) ─────────────────────────────────
export async function listDevelopers(
  projectId: string,
): Promise<{ total: number; items: CcDeveloper[] }> {
  const res = await apiClient.get(`${base(projectId)}/developers`);
  return res.data;
}

// ---------------------------------------------------------------------------
// Trace · progresso fino do deploy (CC.31 fatia 1)
// ---------------------------------------------------------------------------
//
// O rollup por objeto existe no banco desde o CC.12 e até aqui só era alcançável
// por tool MCP, ou seja, pelo Claude do dev no terminal. É a causa medida da
// opacidade: o board mostra status de pacote e nada abaixo disso.

// O backend separa dois conceitos que antes eram uma lista só (S-060): o vocabulário
// do que pode ser gravado no ledger, e o subconjunto que decide conclusão. O frontend
// espelha a separação porque ela é justamente o que a matriz precisa.

/**
 * As fases do pipeline de entrega de um objeto, na ordem canônica. Espelha
 * `REQUIRED_PHASES` no backend: são estas que `_gates_concluded` cobra.
 *
 * É a lista que a matriz objeto-por-fase usa como colunas.
 */
export const CC_PHASES = [
  "preflight",
  "deploy",
  "activate",
  "syntax",
  "atc",
  "abap_unit",
] as const;

/**
 * Tudo que o ledger aceita gravar (`PHASE_VOCABULARY` no backend).
 *
 * `delete` está aqui e **não** em `CC_PHASES` de propósito: deleção é um evento do
 * ciclo de vida, não uma etapa do pipeline de entrega. Como coluna da matriz ela
 * ficaria vazia em quase todo objeto, e coluna que nunca preenche faz a linha mentir
 * ("1 fase pendente" para sempre) mesmo com a célula honesta. Serve para superfícies
 * que mostram histórico ou linha do tempo.
 */
export const CC_PHASE_VOCABULARY = [...CC_PHASES, "delete"] as const;

export type CcPhase = (typeof CC_PHASE_VOCABULARY)[number];
/** Fase que participa do veredito de conclusão. */
export type CcRequiredPhase = (typeof CC_PHASES)[number];

export interface CcChangeSetSummary {
  id: number;
  status: string;
  created_at: string | null;
  object_count: number;
}

/** Os três atores que escrevem no ledger do Code Companion. */
export type CcActorType = "autonomous" | "mcp" | "human";

export interface CcObjectDeployStatus {
  object_name: string;
  /**
   * Nome não identifica objeto ABAP: a convenção RAP batiza a behavior definition com
   * o nome da view raiz, então o mesmo nome existe como `DDLS` e como `BDEF`. A chave
   * da linha é `(nome, tipo)`, e é o que a matriz usa como React key.
   */
  object_type: string;
  /** fase → ok. Fase **ausente** = ainda não rodou, que é diferente de ter falhado. */
  phases: Partial<Record<CcPhase, boolean>>;
  /**
   * fase → texto que o SAP devolveu (CC.34).
   *
   * Mapa separado do `phases` porque o backend usa `phases` como bool no
   * `_gates_concluded`, que é o mesmo predicado do ready gate. Chave **ausente**
   * significa que o SAP não disse nada naquela fase, e não que disse nada.
   *
   * Opcional porque runs anteriores à CC.34 e o cache do react-query trazem o
   * formato antigo.
   */
  messages?: Partial<Record<CcPhase, string>>;
  /**
   * fase → ms que a chamada ao alvo levou (S-064 onda 2).
   *
   * Chave **ausente** = não foi medida: linha anterior à onda, ou fase que acontece
   * dentro de outra (a ativação roda dentro da escrita, e repetir o tempo dela
   * contaria o mesmo intervalo duas vezes). `0` diria "instantâneo".
   */
  durations?: Partial<Record<CcPhase, number>>;
  /**
   * fase → quem gravou (S-064 onda 2).
   *
   * `autonomous` é o run no worker, `mcp` é o Claude do dev no terminal dele, `human`
   * é alguém clicando. Chave **ausente** = linha anterior à onda; chutar seria pior
   * que admitir que não se sabe.
   */
  actors?: Partial<Record<CcPhase, CcActorType>>;
  concluded: boolean;
}

export interface CcDeployStatus {
  change_set_id: number;
  objects: CcObjectDeployStatus[];
  all_concluded: boolean;
}

export async function listChangeSets(
  projectId: string,
  packageId: number,
): Promise<{ total: number; items: CcChangeSetSummary[] }> {
  const res = await apiClient.get(
    `/api/projects/${projectId}/code-companion/packages/${packageId}/change-sets`,
  );
  return res.data;
}

export async function getDeployStatus(
  projectId: string,
  changeSetId: number,
): Promise<CcDeployStatus> {
  const res = await apiClient.get(
    `/api/projects/${projectId}/code-companion/change-sets/${changeSetId}/deploy-status`,
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Grafo e transporte (S-064 onda 6)
// ---------------------------------------------------------------------------

export interface CcGraphEdge {
  /** `from` depende de `to`: a aresta aponta para o pré-requisito. */
  from: string;
  to: string;
}

export interface CcObjectGraph {
  package_id: number;
  nodes: string[];
  edges: CcGraphEdge[];
  build_order: string[];
  /** A de build ao contrário. **Vazia quando há ciclo**: ordem parcial invertida seria
   * um plano de rollback que não fecha. */
  rollback_order: string[];
  cycle: boolean;
  cycle_nodes: string[];
}

export async function getObjectGraph(
  projectId: string,
  packageId: number,
): Promise<CcObjectGraph> {
  const res = await apiClient.get(
    `/api/projects/${projectId}/code-companion/packages/${packageId}/graph`,
  );
  return res.data;
}

export interface CcTransportEvent {
  transport_id: string;
  action: string;
  ok: boolean;
  message: string | null;
  actor_type: CcActorType | null;
  recorded_at: string | null;
}

export async function getTransportHistory(
  projectId: string,
  packageId: number,
): Promise<{ total: number; items: CcTransportEvent[] }> {
  const res = await apiClient.get(
    `/api/projects/${projectId}/code-companion/packages/${packageId}/transport`,
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Paridade e escalação (S-064 onda 5)
// ---------------------------------------------------------------------------

export interface CcParityCase {
  case_id: string;
  object_name: string | null;
  /** `match`, `drift`, `error` ou `pending`. `error` conta como divergência. */
  verdict: string;
}

export interface CcParityStatus {
  package_id: number;
  cases: CcParityCase[];
  /** Pacote sem caso nenhum **não** é `all_match`: zero casos é ausência de prova. */
  all_match: boolean;
  has_drift: boolean;
  pending: boolean;
}

export async function getParity(
  projectId: string,
  packageId: number,
): Promise<CcParityStatus> {
  const res = await apiClient.get(
    `/api/projects/${projectId}/code-companion/packages/${packageId}/parity`,
  );
  return res.data;
}

export interface CcEscalation {
  id: number;
  kind: string | null;
  object_name: string | null;
  question: Record<string, unknown>;
  /** Respostas fechadas: é o que permite botões em vez de campo de texto livre. */
  options: string[] | null;
  answer: Record<string, unknown> | null;
  answered_by: string | null;
  actor_type: CcActorType | null;
  asked_at: string | null;
  answered_at: string | null;
  /** Derivado da ausência de resposta. */
  open: boolean;
}

export async function getEscalations(
  projectId: string,
  runId: string,
): Promise<{ total: number; items: CcEscalation[] }> {
  const res = await apiClient.get(
    `/api/projects/${projectId}/code-companion/runs/${runId}/escalations`,
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Manifesto, diff e snapshot do gate (S-064 onda 4)
// ---------------------------------------------------------------------------

export interface CcManifestVersion {
  version: number;
  /** `draft` (proposta), `approved` (em vigor), `superseded` (trocada). */
  status: string;
  manifest: Record<string, unknown>;
  created_at: string | null;
  approved_at: string | null;
}

export interface CcObjectManifestHistory {
  object_name: string;
  object_type: string | null;
  /** Vem do servidor: é a mesma precedência que o change set obedece. */
  current_version: number | null;
  versions: CcManifestVersion[];
}

export async function getManifests(
  projectId: string,
  packageId: number,
  objectName?: string,
): Promise<{ total: number; items: CcObjectManifestHistory[] }> {
  const res = await apiClient.get(
    `/api/projects/${projectId}/code-companion/packages/${packageId}/manifests`,
    objectName ? { params: { object_name: objectName } } : undefined,
  );
  return res.data;
}

export interface CcChangeSetObjectDetail {
  object_name: string;
  object_type: string | null;
  manifest_version: number | null;
  status: string;
  source: string | null;
  /** O ABAP do ECC, guardado na transformação. `null` em objeto migrado antes da onda 4. */
  legacy_source: string | null;
  test_source: string | null;
  /** `null` é "não sei o que era"; `[]` seria "não mudou nada". Coisas opostas. */
  diff: string[] | null;
}

/** Lê um objeto do change set pelo endereço canônico (S-072, F0).
 *
 * ✅ **A identidade é (nome, tipo).** A convenção RAP batiza a behavior definition com o
 * nome da view raiz, então `ZI_PEDIDO` existe como DDLS e como BDEF no mesmo change set.
 * Sem o tipo o servidor responde 409 com a lista de candidatos, em vez de devolver um
 * dos dois por acaso, que era o comportamento antigo.
 *
 * ⚠️ **`encodeURIComponent` não é zelo.** `/EXED/ZCL_FOO` é nome válido de objeto e tem
 * barras dentro: sem encodar, a URL colapsa e o servidor recebe segmentos a mais. O par
 * disto no backend é o conversor `:path` na rota, e um sem o outro não funciona.
 *
 * `objectType` é opcional para não matar link antigo, que continua abrindo enquanto o
 * nome for único no change set.
 */
export async function getChangeSetObject(
  projectId: string,
  changeSetId: number,
  objectName: string,
  objectType?: string,
): Promise<CcChangeSetObjectDetail> {
  // `base` é o helper do módulo, e não uma string local: sombreá-lo faria a próxima
  // edição desta função quebrar em runtime com "base is not a function".
  const objects = `${base(projectId)}/change-sets/${changeSetId}/objects`;
  const url = objectType
    ? `${objects}/${encodeURIComponent(objectType)}/${encodeURIComponent(objectName)}`
    : `${objects}/${encodeURIComponent(objectName)}`;
  const res = await apiClient.get(url);
  return res.data;
}

export interface CcGateEvaluation {
  id: number;
  gate: string;
  ok: boolean;
  missing: string[];
  objects: Record<string, unknown>[];
  /** O que passou raspando. Não bloqueia, e é por isso que precisa aparecer. */
  warnings: string[];
  actor_type: CcActorType | null;
  evaluated_at: string | null;
}

export async function getGateHistory(
  projectId: string,
  packageId: number,
): Promise<{ total: number; items: CcGateEvaluation[] }> {
  const res = await apiClient.get(
    `/api/projects/${projectId}/code-companion/packages/${packageId}/gate-history`,
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Narrativa da autocorreção (S-064 onda 1)
// ---------------------------------------------------------------------------

/** Uma tentativa de autocorreção, com o que o modelo contou e o que cobrou. */
export interface CcCorrectionAttempt {
  attempt_no: number;
  /** Fase que originou o problema: `activate`, `atc` ou `parity`. */
  error_class: string | null;
  /** Quem tentou: o run, o terminal do dev, ou uma pessoa na tela. */
  actor_type: CcActorType | null;
  /** O veredito veio da rodada seguinte: a correção pegou ou não. */
  resolved: boolean;
  /** Teto de tentativas estourado, ou o mesmo erro voltou e o laço desistiu. */
  escalated: boolean;
  /** A leitura do modelo sobre o erro, e o que ele mudou. Vêm do próprio modelo. */
  hypothesis: string | null;
  change_summary: string | null;
  /**
   * `applied` (o fixer produziu source novo) ou `refused` (declinou). Diferente de
   * `resolved`: um pode ser aplicado e não resolver.
   */
  result: string | null;
  /** O que o alvo reclamou, literal. */
  sap_finding: string | null;
  source_hash_before: string | null;
  source_hash_after: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cost_usd: number | null;
  duration_ms: number | null;
}

export interface CcObjectCorrections {
  object_name: string;
  attempts: number;
  last_error: string | null;
  resolved: boolean;
  escalated: boolean;
  open: boolean;
  history: CcCorrectionAttempt[];
  input_tokens: number | null;
  output_tokens: number | null;
  /** `null` é "não chamou o modelo", que é diferente de custar zero. */
  cost_usd: number | null;
}

export interface CcCorrectionStatus {
  change_set_id: number;
  objects: CcObjectCorrections[];
  any_escalated: boolean;
  has_open: boolean;
  all_resolved: boolean;
  input_tokens: number | null;
  output_tokens: number | null;
  cost_usd: number | null;
}

export async function getCorrections(
  projectId: string,
  changeSetId: number,
  objectName?: string,
): Promise<CcCorrectionStatus> {
  const res = await apiClient.get(
    `/api/projects/${projectId}/code-companion/change-sets/${changeSetId}/corrections`,
    objectName ? { params: { object_name: objectName } } : undefined,
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Run autônomo (S-060, fatia 7)
// ---------------------------------------------------------------------------

/** Estados do run. `partial` é desfecho próprio: entregou parte, e isso não é sucesso. */
export const CC_RUN_TERMINAL = [
  "completed",
  "partial",
  "failed",
  "cancelled",
] as const;

export type CcRunStatus =
  | "queued"
  | "running"
  | "paused"
  | "cancelling"
  | (typeof CC_RUN_TERMINAL)[number];

/**
 * Motivo da pausa. A tela precisa distinguir "parou esperando você" de "parou por
 * erro", porque a ação é diferente: `no_adt_session` pede reconectar, `hitl` pede
 * decisão, e `auth` pede credencial.
 */
export type CcPauseReason =
  | "hitl"
  | "auth"
  | "no_adt_session"
  /** O provedor do modelo não respondeu (#603). Fica ao lado do `no_adt_session`:
   *  credencial ou serviço de fora, com remédio conhecido e fora do escopo da
   *  entrega. O que não pode é isso virar pergunta sobre abandonar objeto. */
  | "model_unavailable"
  | "manual";

/**
 * Estados de uma linha do roster de drafting (CC.34 F2), espelhando
 * `app/services/cc_draft_repo.DRAFT_STATUSES`.
 *
 * A distinção que a tela **precisa** respeitar: `needs_redesign` e `unsupported` não são
 * falha. São entrega a uma pessoa — o objeto sai do lote autônomo e vira trabalho de um
 * dev com o Claude no modo assistido. Pintá-los de vermelho manda o dev procurar bug
 * onde há veredito do alvo.
 */
export const CC_DRAFT_HANDOFF = ["needs_redesign", "unsupported"] as const;

export type CcDraftStatus =
  "pending" | "drafted" | "needs_redesign" | "unsupported" | "failed";

export interface CcDraftingSummary {
  total: number;
  drafted: number;
  /** `needs_redesign` + `unsupported`: o que espera um dev, não o que quebrou. */
  handoff: number;
  failed: number;
  pending: number;
  by_status: Partial<Record<CcDraftStatus, number>>;
}

export interface CcDraftStateItem {
  object_name: string;
  /** Tipo TADIR na origem (ECC) e tipo no alvo (GROW). São duas coisas: `ZI033114` é
   * `SXCI` num e `CLAS` no outro, e confundi-los deixava o objeto intransponível. */
  source_type: string | null;
  target_type: string | null;
  target_name: string | null;
  status: CcDraftStatus;
  attempts: number;
  last_error: string | null;
  next_attempt_at: string | null;
  change_set_id: number | null;
  module: string | null;
}

export interface CcDraftingState {
  package_id: number;
  summary: CcDraftingSummary;
  items: CcDraftStateItem[];
}

export function isDraftHandoff(status: CcDraftStatus): boolean {
  return (CC_DRAFT_HANDOFF as readonly string[]).includes(status);
}

export interface CcRunState {
  run_id: string;
  status: CcRunStatus;
  pause_reason: CcPauseReason | null;
  package_id: number;
  change_set_id: number | null;
  execution_id: string | null;
  /**
   * Contagem do drafting. Sem ela os primeiros minutos de um run grande mostram zero
   * objetos e zero progresso, que na tela é indistinguível de travado — e é a fase mais
   * longa, porque cada objeto custa uma chamada ao modelo.
   */
  drafting?: CcDraftingSummary | null;
  total_objects: number;
  concluded_objects: number;
  actual_cost_usd: number;
  /**
   * Marcas cruas do run (S-064 onda 2). O decorrido é contado na tela: um `elapsed_ms`
   * vindo do servidor congelaria entre dois polls, e run em andamento precisa que o
   * número ande.
   */
  started_at?: string | null;
  paused_at?: string | null;
  finished_at?: string | null;
  /** Payload do HITL, relido no F5. Sem ele a pausa fica muda. */
  question: Record<string, unknown> | null;
  /**
   * Por que o run terminou assim, quando há o que dizer (#576). `null` em run completo:
   * explicar o sucesso é ruído. Gravar isto no banco fechava só metade da issue, cujo
   * título é "e a tela não tem o que dizer".
   */
  error?: string | null;
  objects: CcObjectDeployStatus[];
}

export function isRunTerminal(status: CcRunStatus): boolean {
  return (CC_RUN_TERMINAL as readonly string[]).includes(status);
}

/**
 * Este usuário pode disparar e controlar o run? (CC.34 F6)
 *
 * As quatro rotas que controlam o run exigem `consultant`, seguindo a regra da casa
 * para escrita dentro de um projeto (S-056 R5/R9). Sem o mesmo corte aqui, quem tem
 * papel `user` vê o botão, clica e toma 403 sem explicação.
 *
 * Não substitui a autorização, que é do servidor: isto só evita oferecer uma ação
 * que sempre falharia. Ler o progresso continua aberto a `user`.
 *
 * A tabela de níveis morava aqui e saiu para [`lib/roles.ts`](../lib/roles.ts) na
 * Fase 5 da S-073 — era a única hierarquia de papéis do frontend, escondida num
 * cliente de API, e a partir dela o gate de rota e o da sidebar teriam feito uma
 * segunda cópia. `hasMinRole` é fail-closed pelo mesmo motivo que este era.
 */
export function canControlRun(role: string | null | undefined): boolean {
  return hasMinRole(role, "consultant");
}

export async function createRun(
  projectId: string,
  packageId: number,
  body: {
    change_set_id?: number;
    only?: { object_name: string; object_type: string }[];
    /**
     * Retentar o que já rodou (S-064 onda 3).
     *
     * Sem isto um objeto reprovado é terminal e o run finaliza na hora sem executar
     * nada. A marca faz o plano ignorar as fases anteriores; nada é apagado do ledger.
     */
    retry?: boolean;
  } = {},
): Promise<{
  run_id: string;
  status: CcRunStatus;
  execution_id: string | null;
}> {
  const res = await apiClient.post(
    `/api/projects/${projectId}/code-companion/packages/${packageId}/runs`,
    body,
  );
  return res.data;
}

export async function getRun(
  projectId: string,
  runId: string,
): Promise<CcRunState> {
  const res = await apiClient.get(
    `/api/projects/${projectId}/code-companion/runs/${runId}`,
  );
  return res.data;
}

export async function pauseRun(
  projectId: string,
  runId: string,
  reason = "manual",
) {
  const res = await apiClient.post(
    `/api/projects/${projectId}/code-companion/runs/${runId}/pause`,
    { reason },
  );
  return res.data;
}

export async function resumeRun(
  projectId: string,
  runId: string,
  answer?: Record<string, unknown>,
) {
  const res = await apiClient.post(
    `/api/projects/${projectId}/code-companion/runs/${runId}/resume`,
    { answer },
  );
  return res.data;
}

export async function cancelRun(projectId: string, runId: string) {
  const res = await apiClient.post(
    `/api/projects/${projectId}/code-companion/runs/${runId}/cancel`,
  );
  return res.data;
}

/** O run corrente do pacote, ou `null` se nunca rodou. */
export async function getPackageRun(
  projectId: string,
  packageId: number,
): Promise<CcRunState | null> {
  const res = await apiClient.get(
    `/api/projects/${projectId}/code-companion/packages/${packageId}/run`,
  );
  return res.data ?? null;
}

/**
 * O roster de drafting do pacote: o que já virou source, o que falta, e o que foi
 * entregue a uma pessoa.
 *
 * É a resposta a "por que o pacote tem 33 objetos e o change set tem 12". Sem ela a
 * tela só sabe contar o que está no change set, e os outros 21 somem sem explicação.
 */
export async function getPackageDrafting(
  projectId: string,
  packageId: number,
): Promise<CcDraftingState> {
  const res = await apiClient.get(
    `/api/projects/${projectId}/code-companion/packages/${packageId}/drafting`,
  );
  return res.data;
}
